import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static mobileEagerMinCount = 4
  static mobileEagerMaxCount = 6
  static mobilePreloadRootMargin = "1500px 0px"
  static desktopPreloadRootMargin = "300px 0px"

  static targets = ["container", "source"]
  static values = {
    minHeight: Number,
    spacing: Number,
    mobileBreakpoint: Number
  }

  connect() {
    this.handleResize = this.handleResize.bind(this)
    this.render = this.render.bind(this)
    this.renderWhenVisible = this.renderWhenVisible.bind(this)
    this.loadObservedImage = this.loadObservedImage.bind(this)
    this.lastRenderWidth = null
    this.lastLayoutMode = null

    window.addEventListener("resize", this.handleResize)
    this.element.addEventListener("responsive-gallery:render", this.renderWhenVisible)
    this.renderWhenVisible()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.element.removeEventListener("responsive-gallery:render", this.renderWhenVisible)
    this.teardownObserver()
  }

  handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      if (!this.shouldRerenderForResize()) {
        return
      }

      this.renderWhenVisible()
    }, 150)
  }

  render() {
    this.recordRenderMetrics()
    this.setupObserver()
    this.teardownObservedTargets()
    this.containerTarget.replaceChildren()

    const items = this.sourceImages
    if (items.length === 0) {
      return
    }

    if (window.innerWidth < this.mobileBreakpointValue) {
      this.buildMobileLayout(items)
      return
    }

    this.buildRows(items)
  }

  renderWhenVisible() {
    if (this.isHidden()) {
      return
    }

    this.render()
  }

  buildMobileLayout(items) {
    const containerWidth = this.containerTarget.clientWidth || this.element.clientWidth || window.innerWidth
    const eagerCount = this.mobileEagerCount(items, containerWidth)

    items.forEach((item, index) => {
      const row = document.createElement("div")
      row.className = "mb-3"
      row.appendChild(this.buildTrigger(item, {
        width: "100%",
        height: "auto",
        aspectRatio: item.intrinsicWidth > 0 && item.intrinsicHeight > 0 ? `${item.intrinsicWidth} / ${item.intrinsicHeight}` : `${item.ratio}`
      }, index < eagerCount))
      this.containerTarget.appendChild(row)
    })
  }

  buildRows(items) {
    const containerWidth = this.containerTarget.clientWidth || this.element.clientWidth
    let currentIndex = 0
    let previousRowHeight = null

    while (currentIndex < items.length) {
      let bestRow = [items[currentIndex]]

      for (let count = 2; count <= Math.min(6, items.length - currentIndex); count += 1) {
        const testRow = items.slice(currentIndex, currentIndex + count)
        const totalSpacing = (testRow.length - 1) * this.spacingValue
        const availableWidth = containerWidth - totalSpacing
        const totalRatio = testRow.reduce((sum, item) => sum + item.ratio, 0)
        const calculatedHeight = availableWidth / totalRatio

        if (calculatedHeight >= this.minHeightValue) {
          bestRow = testRow
        } else {
          break
        }
      }

      const isLastRow = currentIndex + bestRow.length >= items.length
      const totalSpacing = (bestRow.length - 1) * this.spacingValue
      const availableWidth = containerWidth - totalSpacing
      const totalRatio = bestRow.reduce((sum, item) => sum + item.ratio, 0)

      let rowHeight = Math.max(this.minHeightValue, availableWidth / totalRatio)
      if (isLastRow && previousRowHeight !== null) {
        rowHeight = previousRowHeight
      }

      let actualRowWidth = containerWidth
      let shouldCenter = false
      if (isLastRow && previousRowHeight !== null && rowHeight === previousRowHeight) {
        actualRowWidth = bestRow.reduce((sum, item) => sum + rowHeight * item.ratio, 0) + totalSpacing
        shouldCenter = actualRowWidth < containerWidth
      }

      const row = document.createElement("div")
      row.className = "mb-3 flex"
      row.style.gap = `${this.spacingValue}px`
      row.style.width = `${containerWidth}px`

      if (shouldCenter) {
        row.style.justifyContent = "center"
      }

      bestRow.forEach((item) => {
        row.appendChild(this.buildTrigger(item, {
          width: `${rowHeight * item.ratio}px`,
          height: `${rowHeight}px`
        }, currentIndex === 0))
      })

      this.containerTarget.appendChild(row)
      previousRowHeight = rowHeight
      currentIndex += bestRow.length
    }
  }

  buildTrigger(item, dimensions, eagerLoad = false) {
    const wrapper = document.createElement("div")
    wrapper.style.width = dimensions.width

    if (item.durationLabel) {
      wrapper.className = "responsive-gallery__video-card"
    } else {
      wrapper.style.height = dimensions.height
    }

    if (dimensions.aspectRatio) {
      if (item.durationLabel) {
        wrapper.style.aspectRatio = ""
      } else {
        wrapper.style.aspectRatio = dimensions.aspectRatio
        wrapper.style.overflow = "hidden"
      }
    }

    const button = document.createElement("button")
    button.type = "button"
    button.className = "block h-full w-full text-left"
    if (item.durationLabel) {
      button.style.width = "100%"
      button.style.height = dimensions.height
      if (dimensions.aspectRatio) {
        button.style.aspectRatio = dimensions.aspectRatio
        button.style.overflow = "hidden"
      }
    }
    this.assignTriggerBehavior(button, item)

    const image = document.createElement("img")
    image.alt = item.caption
    image.loading = eagerLoad ? "eager" : "lazy"
    image.decoding = "async"
    image.className = "responsive-gallery__image block h-full w-full bg-zinc-100 object-cover"
    image.style.width = dimensions.width
    image.style.height = dimensions.height
    image.width = item.intrinsicWidth || undefined
    image.height = item.intrinsicHeight || undefined
    image.dataset.gridUrl = item.gridUrl
    image.dataset.loaded = "false"
    image.addEventListener("load", () => this.markImageLoaded(image))

    if (eagerLoad || !this.observer) {
      this.loadObservedImage(image)
    } else {
      this.observer.observe(image)
    }

    button.appendChild(image)
    wrapper.appendChild(button)

    if (item.durationLabel) {
      const duration = document.createElement("p")
      duration.className = "mt-2 text-xs font-medium uppercase tracking-[0.18em] text-stone-500"
      duration.textContent = item.durationLabel
      wrapper.appendChild(duration)
    }

    return wrapper
  }

  get sourceImages() {
    return Array.from(this.sourceTarget.querySelectorAll("[data-index]")).map((image) => {
      const width = Number.parseInt(image.getAttribute("width") || "0", 10)
      const height = Number.parseInt(image.getAttribute("height") || "0", 10)
      const ratio = width > 0 && height > 0 ? width / height : 4 / 3

      return {
        kind: image.dataset.kind || "image",
        index: Number.parseInt(image.dataset.index, 10),
        caption: image.dataset.caption,
        gridUrl: image.dataset.gridUrl,
        lightboxFetchUrl: image.dataset.lightboxFetchUrl,
        fullsizeFetchUrl: image.dataset.fullsizeFetchUrl,
        fileUrl: image.dataset.fileUrl,
        playEventUrl: image.dataset.playEventUrl,
        durationLabel: image.dataset.durationLabel,
        intrinsicWidth: width,
        intrinsicHeight: height,
        ratio
      }
    })
  }

  assignTriggerBehavior(button, item) {
    if (item.kind === "video") {
      button.dataset.action = "video-lightbox#open"
      button.dataset.videoLightboxTarget = "item"
      button.dataset.videoLightboxIndex = item.index
      button.dataset.videoLightboxSrc = item.fileUrl
      button.dataset.videoLightboxCaption = item.caption
      button.dataset.videoLightboxPlayEventUrl = item.playEventUrl
      return
    }

    button.dataset.action = "lightbox#open"
    button.dataset.lightboxTarget = "item"
    button.dataset.lightboxIndex = item.index
    button.dataset.lightboxFetchUrl = item.lightboxFetchUrl
    button.dataset.fullsizeFetchUrl = item.fullsizeFetchUrl
    button.dataset.intrinsicWidth = item.intrinsicWidth || 0
    button.dataset.intrinsicHeight = item.intrinsicHeight || 0
    button.dataset.caption = item.caption
  }

  setupObserver() {
    if (!("IntersectionObserver" in window)) {
      this.teardownObserver()
      this.observer = null
      return
    }

    const rootMargin = this.observerRootMargin()
    if (this.observer && this.observerRootMarginValue === rootMargin) {
      return
    }

    this.teardownObserver()
    this.observerRootMarginValue = rootMargin
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        this.loadObservedImage(entry.target)
        this.observer.unobserve(entry.target)
      })
    }, {
      rootMargin
    })
  }

  teardownObserver() {
    if (!this.observer) {
      return
    }

    this.observer.disconnect()
    this.observer = null
  }

  teardownObservedTargets() {
    if (!this.observer) {
      return
    }

    this.containerTarget.querySelectorAll("img[data-grid-url]").forEach((image) => {
      this.observer.unobserve(image)
    })
  }

  loadObservedImage(image) {
    if (!image.dataset.gridUrl || image.getAttribute("src") === image.dataset.gridUrl) {
      return
    }

    image.src = image.dataset.gridUrl

    if (image.complete) {
      this.markImageLoaded(image)
    }
  }

  observerRootMargin() {
    return this.isMobileViewport() ? this.constructor.mobilePreloadRootMargin : this.constructor.desktopPreloadRootMargin
  }

  isMobileViewport() {
    return window.innerWidth < this.mobileBreakpointValue
  }

  currentLayoutMode() {
    return this.isMobileViewport() ? "mobile" : "desktop"
  }

  currentRenderWidth() {
    return this.containerTarget.clientWidth || this.element.clientWidth || window.innerWidth || 0
  }

  isHidden() {
    return this.element.classList.contains("hidden")
  }

  shouldRerenderForResize() {
    const nextLayoutMode = this.currentLayoutMode()
    const nextWidth = this.currentRenderWidth()

    if (this.lastLayoutMode !== nextLayoutMode) {
      return true
    }

    return Math.abs(nextWidth - (this.lastRenderWidth || 0)) >= 2
  }

  recordRenderMetrics() {
    this.lastLayoutMode = this.currentLayoutMode()
    this.lastRenderWidth = this.currentRenderWidth()
  }

  mobileEagerCount(items, containerWidth) {
    const viewportHeight = window.innerHeight || 800
    const targetHeight = viewportHeight * 2
    let totalHeight = 0
    let count = 0

    while (count < items.length && count < this.constructor.mobileEagerMaxCount) {
      const item = items[count]
      const estimatedHeight = containerWidth / item.ratio
      totalHeight += estimatedHeight
      count += 1

      if (count >= this.constructor.mobileEagerMinCount && totalHeight >= targetHeight) {
        break
      }
    }

    return count
  }

  markImageLoaded(image) {
    image.dataset.loaded = "true"
  }
}
